import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, AlarmClock, Stethoscope, Briefcase, KeyRound, HeartPulse, Landmark, AlertOctagon, Scale } from 'lucide-react';
const tomos = [{
  num: 'T1',
  icon: AlarmClock,
  title: 'Protocolo de las primeras 48 horas',
  tag: 'Urgente',
  desc: 'Lo que hacés (o no hacés) en las primeras 48 horas define si ganás o perdés tu reclamo.',
  bullets: ['Las 3 reglas de oro antes de hacer cualquier cosa', 'Checklist de acción inmediata con casilleros marcables', 'Cómo documentar tu caso desde cero', 'Modelo de constancia lista para enviar']
}, {
  num: 'T2',
  icon: Stethoscope,
  title: 'Tu reclamo ante la ART paso a paso',
  tag: 'ART',
  desc: 'Accidentes de trabajo, altas médicas anticipadas e incapacidades: cómo reclamar sin abogado.',
  bullets: ['Tus derechos básicos que la ART nunca te va a contar', 'Cómo rechazar el alta médica por escrito', 'Carta de impugnación lista para completar y enviar', 'Cómo verificar si tu indemnización es correcta']
}, {
  num: 'T3',
  icon: Briefcase,
  title: 'Tus derechos laborales en situaciones críticas',
  tag: 'Laboral',
  desc: 'Despidos, suspensiones, descuentos de sueldo y presiones ilegales: lo que tu empleador no quiere que sepas.',
  bullets: ['Cómo calcular lo que te corresponde en un despido', 'Telegrama laboral gratuito: qué es y cómo enviarlo', 'La renuncia forzada: cómo identificarla y qué hacer', 'Modelos de telegrama e intimación listos para usar']
}, {
  num: 'T4',
  icon: KeyRound,
  title: 'Inquilino informado: lo que el propietario no puede hacerte',
  tag: 'Alquiler',
  desc: 'Aumentos, desalojos, garantías y reparaciones: tus derechos reales como inquilino.',
  bullets: ['Cuándo un aumento de alquiler es ilegal', 'El desalojo por mano propia es un delito penal', 'Cómo recuperar tu depósito aunque el propietario se niegue', 'Carta de respuesta a intimaciones ilegales lista para enviar']
}, {
  num: 'T5',
  icon: HeartPulse,
  title: 'Obra social y prepaga: cómo lograr que te den lo que te corresponde',
  tag: 'Salud',
  desc: 'Coberturas negadas, medicamentos, tratamientos y cirugías: el protocolo de reclamo que funciona.',
  bullets: ['El PMO en lenguaje humano: qué están obligados a cubrir', 'El amparo judicial que se resuelve en 24 a 72 horas', 'Salud mental: la cobertura que más se niega y más se desconoce', 'Carta de reclamo formal lista para presentar']
}, {
  num: 'T6',
  icon: Landmark,
  title: 'Banco, tarjeta y servicios: reclamá lo que te cobraron de más',
  tag: 'Banco',
  desc: 'Cobros indebidos, débitos no autorizados y rechazos injustificados: cómo recuperar tu plata.',
  bullets: ['La cuenta sueldo es gratuita por ley — ¿te la están cobrando?', 'Cómo dar de baja un débito automático sin hablar con la empresa', 'Aumentos de telefonía sin aviso: podés rescindir sin penalidad', 'Carta de reclamo ante BCRA y Defensa del Consumidor lista']
}, {
  num: 'T7',
  icon: AlertOctagon,
  title: 'Los 30 errores que arruinan cualquier reclamo',
  tag: 'Exclusivo',
  isExclusive: true,
  desc: 'No perdés tus reclamos porque no tenés razón. Los perdés porque no sabés cómo presentarlos.',
  bullets: ['Los 6 errores que se cometen en las primeras horas', 'Los errores por desconocer los plazos legales', 'Qué hacer si ya cometiste alguno de estos errores', 'Checklist de control antes de presentar cualquier reclamo']
}, {
  num: 'T8',
  icon: Scale,
  title: 'Cuándo sí necesitás un abogado y cómo no dejarte estafar por uno',
  tag: 'Exclusivo',
  isExclusive: true,
  desc: 'Cómo evaluar si tu caso lo justifica, qué honorarios son razonables y qué cláusulas no firmar jamás.',
  bullets: ['Las 10 preguntas que tenés que hacerle a un abogado', 'Honorarios razonables: rangos reales en Argentina', 'Las 5 señales de que tu abogado no está haciendo bien su trabajo', 'Alternativas gratuitas al abogado privado']
}];
export function Tomos() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const toggleTomo = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };
  return <section className="py-12 md:py-[80px] px-5 bg-white" data-id="element-279">
      <div className="max-w-[860px] mx-auto" data-id="element-280">
        <div className="text-[11px] font-extrabold tracking-[0.14em] uppercase text-blue mb-2" data-id="element-281">
          Contenido completo
        </div>
        <h2 className="font-display text-[clamp(28px,6vw,52px)] text-navy mb-2 leading-tight" data-id="element-282">
          Los 8 tomos
        </h2>
        <p className="text-[15px] md:text-[16px] text-text-muted max-w-[560px] mb-6 md:mb-10" data-id="element-283">
          Tu hoja de ruta para defender tus derechos en cualquier situación.
        </p>

        <div className="flex flex-col gap-3 md:gap-4" data-id="element-284">
          {tomos.map((tomo, i) => {
          const isOpen = openIndex === i;
          const Icon = tomo.icon;
          return <div key={i} className={`border border-border rounded-xl overflow-hidden cursor-pointer transition-shadow hover:shadow-lg ${tomo.isExclusive ? 'border-t-[3px] border-t-gold' : ''}`} onClick={() => toggleTomo(i)} data-id="element-285">
                <div className={`flex items-center gap-2.5 md:gap-4 py-3 px-3 md:py-4.5 md:px-5.5 ${tomo.isExclusive ? 'bg-[#FFF9ED]' : 'bg-sky'}`} data-id="element-286">
                  {/* Topic Icon */}
                  <div className={`flex-shrink-0 w-11 h-11 md:w-14 md:h-14 rounded-lg flex items-center justify-center ${tomo.isExclusive ? 'bg-gold/15 border border-gold/30 text-gold' : 'bg-blue/10 border border-blue/20 text-blue'}`} data-id="element-287">
                    <Icon size={24} strokeWidth={1.8} className="md:w-7 md:h-7" data-id="element-288" />
                  </div>

                  {/* Tomo number */}
                  <span className={`font-display text-[20px] md:text-[26px] min-w-[28px] md:min-w-[36px] ${tomo.isExclusive ? 'text-gold' : 'text-blue'}`} data-id="element-289">
                    {tomo.num}
                  </span>

                  {/* Title */}
                  <h3 className="text-[13px] md:text-[16px] font-bold text-navy flex-1 leading-tight" data-id="element-290">
                    {tomo.title}
                  </h3>

                  {/* Tag — hide on smallest screens to save space */}
                  <span className={`hidden sm:inline-block text-[10px] md:text-[11px] font-bold py-1 px-2.5 md:px-3 rounded-full whitespace-nowrap ${tomo.isExclusive ? 'bg-gold text-navy' : 'bg-blue text-white'}`} data-id="element-291">
                    {tomo.tag}
                  </span>

                  {/* Chevron */}
                  <motion.div animate={{
                rotate: isOpen ? 180 : 0
              }} transition={{
                duration: 0.2
              }} className={`flex-shrink-0 ${tomo.isExclusive ? 'text-gold' : 'text-blue'}`} data-id="element-292">
                    <ChevronDown size={20} data-id="element-293" />
                  </motion.div>
                </div>

                <AnimatePresence data-id="element-294">
                  {isOpen && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.2
              }} className="overflow-hidden" data-id="element-295">
                      <div className="p-4 md:p-4.5 md:px-5.5 text-[13px] md:text-[14px] text-text-muted border-t border-border/50" data-id="element-296">
                        <p className="mb-2.5 md:mb-3" data-id="element-297">{tomo.desc}</p>
                        <ul className="list-disc pl-4 md:pl-4.5 space-y-1" data-id="element-298">
                          {tomo.bullets.map((bullet, j) => <li key={j} className="leading-snug md:leading-normal" data-id="element-299">
                              {bullet}
                            </li>)}
                        </ul>
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </div>;
        })}
        </div>
      </div>
    </section>;
}